import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { resetPassword } = useAuth();
  const nav = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    const res = await resetPassword(token, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Password reset. You're logged in.");
      nav("/account", { replace: true });
    } else {
      setErr(res.error || "Reset failed.");
    }
  };

  if (!token) {
    return (
      <div className="px-4 md:px-8 py-12 md:py-20 min-h-[70vh]" data-testid="reset-page">
        <div className="max-w-md mx-auto border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard p-6 md:p-10">
          <AlertTriangle size={40} className="text-[#D95D39]" />
          <h1 className="font-display text-4xl uppercase leading-none mt-3">No reset token.</h1>
          <p className="font-body text-base mt-3 text-[#1C1914]/80">
            This link is missing the reset token. Request a new one.
          </p>
          <Link to="/forgot-password" className="btn-duds inline-block mt-5">
            Get a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-12 md:py-20 min-h-[70vh]" data-testid="reset-page">
      <div className="max-w-md mx-auto border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard p-6 md:p-10">
        <p className="font-condensed text-sm uppercase tracking-widest text-[#D95D39]">// reset</p>
        <h1 className="font-display text-4xl md:text-5xl uppercase leading-none mt-2">
          New password.
        </h1>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="block font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-1">
              New password <span className="opacity-50">(8+ characters)</span>
            </label>
            <input
              type="password"
              required
              minLength={8}
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-duds"
              data-testid="reset-password"
            />
          </div>
          <div>
            <label className="block font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mb-1">Confirm</label>
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="input-duds"
              data-testid="reset-confirm"
            />
          </div>
          {err && (
            <div className="border-2 border-[#D95D39] bg-[#D95D39]/10 px-3 py-2 font-condensed text-sm uppercase tracking-widest text-[#D95D39]" data-testid="reset-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="btn-duds w-full flex items-center justify-center gap-2"
            data-testid="reset-submit"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Set new password
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
